import type { App } from 'vue';

export const enum CaptchaProvider {
    GOOGLE = 'google',
    YANDEX = 'yandex',
}

let captchaInstance: CaptchaInstanceInterface;
let captchaProvider: CaptchaProvider | undefined;

export function useCaptcha() {
    return {
        captcha: captchaInstance,
        load,
    }
}

export function captchaHeaders(token: string): Record<string, string> {
    if (captchaProvider === CaptchaProvider.YANDEX) {
        return { smartcaptcha: token }
    }

    return { recaptcha: token }
}

export interface CaptchaPluginOptions {
    siteKey: string;
    provider?: CaptchaProvider;
}

export const Captcha = {
    install(app: App, options: CaptchaPluginOptions) {
        captchaInstance = new MockCaptchaInstance()

        if (!options.siteKey || !options.provider) {
            return;
        }

        captchaProvider = options.provider

        if (captchaProvider === CaptchaProvider.YANDEX) {
            captchaInstance = new YandexSmartCaptchaInstance(options)
        } else {
            captchaInstance = new GoogleRecaptchaInstance(options)
        }
    },
};

interface CaptchaInstanceInterface {
    execute(action: string): Promise<string>;
    load(): void
}

class MockCaptchaInstance implements CaptchaInstanceInterface {
    execute(action: string): Promise<string> {
        return Promise.resolve(action);
    }

    load(): void {
        return
    }
}

class GoogleRecaptchaInstance implements CaptchaInstanceInterface {
    private options: CaptchaPluginOptions
    private renderId = ''

    constructor(options: CaptchaPluginOptions) {
        this.options = options
    }

    execute(action: string): Promise<string> {
        if (!window.grecaptcha) {
            throw new Error('recaptcha not loaded');
        }

        return window.grecaptcha.execute(this.renderId, { action: action })
    }

    load() {
        if (!window.grecaptcha) {
            throw new Error('recaptcha not loaded');
        }

        this.renderId = window.grecaptcha.render({
            sitekey: this.options.siteKey,
        })
    }
}

class YandexSmartCaptchaInstance implements CaptchaInstanceInterface {
    private options: CaptchaPluginOptions
    private widgetId: number | undefined
    private pendingResolve: ((token: string) => void) | null = null

    constructor(options: CaptchaPluginOptions) {
        this.options = options
    }

    execute(): Promise<string> {
        if (!window.smartCaptcha || this.widgetId === undefined) {
            throw new Error('smartcaptcha not loaded');
        }

        window.smartCaptcha.reset(this.widgetId)

        return new Promise<string>((resolve) => {
            this.pendingResolve = resolve
            window.smartCaptcha?.execute(this.widgetId)
        })
    }

    load() {
        if (!window.smartCaptcha) {
            throw new Error('smartcaptcha not loaded');
        }

        let container = document.getElementById('captcha-container')
        if (!container) {
            container = document.createElement('div')
            container.id = 'captcha-container'
            container.style.position = 'fixed'
            container.style.top = '0'
            container.style.left = '0'
            container.style.zIndex = '9999'
            document.body.appendChild(container)
        }

        this.widgetId = window.smartCaptcha.render('captcha-container', {
            sitekey: this.options.siteKey,
            invisible: true,
            callback: (token: string) => {
                if (this.pendingResolve) {
                    this.pendingResolve(token)
                    this.pendingResolve = null
                }
            },
        })
    }
}

let loadPromise: Promise<void> | null = null

const load = (): Promise<void> => {
    if (loadPromise) {
        return loadPromise
    }

    loadPromise = new Promise<void>(resolve => {
        const doc: Document = document
        const script: HTMLScriptElement = doc.createElement('script')

        script.async = true

        if (captchaProvider === CaptchaProvider.YANDEX) {
            script.src = 'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload'
        } else {
            script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
        }

        doc.body.appendChild(script);

        script.onload = () => {
            waitForCaptcha(() => {
                captchaInstance.load()
                resolve()
            })
        }
    })

    return loadPromise
}

const waitForCaptcha = (callback: () => void) => {
    if (captchaProvider === CaptchaProvider.YANDEX) {
        if (window.smartCaptcha === undefined) {
            setTimeout(() => waitForCaptcha(callback), 25)
        } else {
            callback()
        }
    } else {
        if (window.grecaptcha === undefined) {
            setTimeout(() => waitForCaptcha(callback), 25)
        } else {
            window.grecaptcha.ready(callback)
        }
    }
}
